<template lang="pug">
  div.login-form
    slot(name="before" v-bind:model="model")
    slot(v-bind:model="model" name="default")
      div(:class="bodySlotClass")
        form(@submit.prevent)
          guest-form-group(:label-text="$t('Логин или email')" :required="true" :has-error="model.errors.has('username')" :error-message="model.errors.getMessage('username')")
            input.form-control(type='text' autocomplete="username" name="username" @keyup.enter="onLoginPressEnter" v-model="model.values.username" :placeholder="$t('Логин или email')" :readonly="isLoginPasswordDisabled" :disabled="isLoginPasswordDisabled")
          template(v-if="!googleCodeMode")
            guest-form-group(:label-text="$t('Пароль')" :required="true" :has-error="model.errors.has('password')" :error-message="model.errors.getMessage('password')")
              input.form-control(ref="passwordInput" type='password' autocomplete="current-password" name="current-password" @keyup.enter="onPasswordPressEnter" v-model="model.values.password" :readonly="isLoginPasswordDisabled" :disabled="isLoginPasswordDisabled" :placeholder="$t('Пароль')")
            .captcha-row(:class="captchaRowClass")
              guest-form-group(:label-text="$t('Код с картинки')" :required='true' :has-error="model.errors.has('captchaCode')"  :error-message="model.errors.getMessage('captchaCode')")
                input.form-control(type='text' :disabled="isLoginPasswordDisabled" v-model="model.values.captchaCode" autocomplete="off" :placeholder="$t('Код с картинки')" @keyup.enter="onCaptchaPressEnter")
              div
                login-captcha(ref="loginCaptcha" :disabled="isLoginPasswordDisabled")
        form(v-if="googleCodeMode" @submit.prevent)
          guest-form-group(:label-text="$t('Код из приложения Authenticator')" :required="true" :has-error="googleCodeModel.errors.has('code')" :error-message="googleCodeModel.errors.getMessage('code')")
            google-code-input(name="googlecode")
              template(v-slot="slotProps")
                input(@keydown.enter.prevent.stop="onGooglecodePressEnter" v-model="googleCodeModel.values.code" autocomplete="one-time-code" name="one-time-code" :readonly="isDisabled" :disabled="isDisabled" :placeholder="$t('Код')").form-control
            //.gcode-input-block
              inline-svg(:src="require('./google-auth-icon.svg')" width="32" height="32")
              input.form-control(type='text' name="googlecode" @keyup.enter="onGooglecodePressEnter" v-model="googleCodeModel.values.code" :readonly="isDisabled" :disabled="isDisabled" :placeholder="$t('Код')")
    slot(name="after" v-bind:model="model" v-bind:googleCodeMode="googleCodeMode")
</template>

<script lang="ts">
import Vue from "vue";
import {Component, Prop, Watch} from 'vue-property-decorator';
import AuthModel, {GooglecodeModel} from '@/core/auth/AuthModel';
import LoginCaptcha from '@/views/pages/auth/login/LoginCaptcha.vue';
import GuestFormGroup from '@/components/forms/guest/GuestFormGroup.vue';
import {AuthResults} from "@/core/auth/Auth";
import InlineSvg from 'vue-inline-svg';
import GoogleCodeInput from "@/components/forms/googlecode/GoogleCodeInput.vue";

@Component({
  name: 'login-form',
  components: {GoogleCodeInput, LoginCaptcha, GuestFormGroup, InlineSvg},
})
export default class LoginForm extends Vue {
  @Prop({default: false})
  disabled!: boolean

  @Prop({default: ''})
  bodySlotClass: any

  @Prop({default: false})
  oauth: boolean

  @Prop({
    default: function () {
      return new AuthModel();
    },
  })
  model!: AuthModel

  googleCodeMode = false
  googleCodeModel: GooglecodeModel = new GooglecodeModel()

  get isLoginPasswordDisabled() {
    return this.googleCodeMode || this.isDisabled;
  }

  get inputGoogleCode() {
    return this.googleCodeModel.values.code.trim();
  }

  @Watch("inputGoogleCode")
  onInputGoogleCode(newVal) {
    if (/^\d{6}$/.test(newVal))
      this.onGooglecodePressEnter();
  }

  onGooglecodePressEnter() {
    this.$emit('2fa-press-enter');
  }

  onPasswordPressEnter() {
    this.$emit('password-press-enter');
  }

  onLoginPressEnter() {
    const pass = `${this.model.values.password}`;
    if (pass.trim().length > 0) this.onPasswordPressEnter();
    else (this.$refs.passwordInput as any).focus();
  }

  onCaptchaPressEnter() {
    this.onPasswordPressEnter();
  }

  resetModel() {
    this.model.values.captchaCode = '';
    if (this.$refs.loginCaptcha) (this.$refs.loginCaptcha as LoginCaptcha).forceUpdate();
  }

  get captchaRowClass() {
    return {
      'fadein-captcha': this.isCaptchaRequired,
    };
  }

  fullResetModel() {
    this.model.options.isCaptchaRequired = false;
    this.resetModel();
  }

  get busyModel(): boolean {
    return this.model.submitStatus.isLoading || this.googleCodeModel.submitStatus.isLoading;
  }

  get isDisabled(): boolean {
    return this.busyModel || this.disabled;
  }

  get isCaptchaRequired(): boolean {
    return this.model.options.isCaptchaRequired || this.model.errors.has('captchaCode');
  }

  passwordSubmit() {
    this.$emit('login-start', this.model);
    this.model.values.oauth = this.oauth;
    return this.model.submit().then((result) => {
      this.$emit('login-end', this.model);
      this.$emit('login-success', this.model, result);
      return result;
    }).catch((err) => {
      //this.resetModel();
      if (this.model.options.isGooglecodeRequired) {
        this.googleCodeMode = true;
        this.model.errors.clear();
        this.googleCodeModel.values.oauth = this.oauth;
        this.googleCodeModel.values.code = '';
        this.googleCodeModel.values.username = this.model.values.username;
        this.googleCodeModel.values.token = this.model.options.nextToken;
        this.$emit('login-2fa-start', this.model, err);
      } else {
        this.$emit('login-end', this.model);
        this.$emit('login-fail', this.model, err);
      }
      throw err;
    });
  }

  googleCodeSubmit() {
    this.$emit('login-2fa-submit', this.googleCodeModel);
    this.model.errors.clear();
    this.googleCodeModel.errors.clear();
    this.googleCodeModel.submit().then((result: AuthResults) => {
      this.googleCodeMode = false;
      this.googleCodeModel.values.code = '';
      this.$emit('login-end', this.googleCodeModel);
      this.$emit('login-2fa-end', this.googleCodeModel);
      this.$emit('login-success', this.googleCodeModel, result);
      return result;
    }).catch((err) => {
      //this.resetModel();
      if (this.googleCodeModel.options.captchaRequired) {
        this.model.options.isCaptchaRequired = true;
        this.$emit('login-2fa-token-update-required', this.googleCodeModel);
        this.model.values.captchaCode = '';
        this.model.errors.setErrors(this.googleCodeModel.errors.errors);
        this.model.errors.set('captchaCode', this.$t('Требуется ввести код с картинки.') as string);
        this.googleCodeMode = false;
      } else
      if (this.googleCodeModel.options.tokenUpdateRequired)
      {
        this.$emit('login-2fa-token-update-required', this.googleCodeModel);
        this.googleCodeMode = false;
      }
      this.$emit('login-2fa-end', this.googleCodeModel);
      this.$emit('login-2fa-fail', this.googleCodeModel);
      throw err;
    })
  }

  submit() {
    if (this.googleCodeMode)
      this.googleCodeSubmit();
    else
      this.passwordSubmit();
  }

  cancel() {
    this.model.errors.clear();
    this.googleCodeModel.errors.clear();
    if (this.googleCodeMode)
      this.googleCodeMode = false;
  }
}
</script>

<style scoped lang="less">
.captcha-row {
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-gap: 20px;
  //align-content: center;
  //align-items: center;
  .captcha-image {
    margin-top: 26px;
  }
}

//.gcode-input-block {
//  display: grid;
//  grid-gap: 15px;
//  grid-template-columns: max-content auto;
//  align-items: center;
//}

@slideDownSpeed: 0.5s;
.captcha-row {
  transition: @slideDownSpeed;
  overflow: hidden;
  max-height: 0;
  margin: 0 -5px;
  padding: 0 5px;

  &.fadein-captcha {
    max-height: 120px;
    //overflow: visible;
    transition: @slideDownSpeed;
  }
}

</style>

<i18n>
{
  "en": {
    "Требуется ввести код с картинки.": "Captcha is required.",
    "Логин или email": "Username or e-mail",
    "Вход": "Log in",
    "Код с картинки": "Code from picture",
    "Пароль": "Password",
    "Войти": "Log in",
    "Авторизация...": "Logging in...",
    "Восстановить пароль": "Remember password",
    "Регистрация": "Registration",
    "Код из приложения Authenticator": "Code from Authenticator app",
    "Код": "Verification code"
  }
}
</i18n>
